// web/logistics.ts — VISIBLE colony logistics (render-only).
//
// The simulation already moves resources internally — Workers carry `cargo` home,
// the colony's `foodStore` rises and is spent on spawns, kills/boxes add genes.
// This layer makes that traffic SEEN without touching the sim: it reads real state
// each frame and paints a living transportation network —
//
//   • pheromone TRAIL fields — a decaying scalar grid that carriers deposit on, so
//     high-traffic food→nest corridors light up as emergent HIGHWAYS;
//   • filling STORAGE nodes — a granary at each nest that rises/falls with foodStore;
//   • COURIER motes — larva couriers emitted on real spawns, gene couriers on real
//     gene arrivals, ferrying between the nest and hive nodes.
//
// It is purely visual: not part of GameSession, never saved, never hashed, and it
// uses Math.random for sparkle (allowed here — this is NOT the deterministic sim).
// Carried-cargo pellets on individual workers are drawn in render.ts (it already
// walks the organisms); everything else lives here.

import { KIND } from '../src/types.ts';
import type { GameSession } from './game.ts';
import { PLAYER_COLONY } from './game.ts';
import type { ViewBox } from './render.ts';
import { colonyColor } from './render.ts';

const FOOD_TAU = 2.6;     // laden-trail persistence (s) — long, so highways linger
const SCOUT_TAU = 1.1;    // forager-trail persistence (s) — shorter, more diffuse
const FOOD_DEP = 1.1;     // laden deposit per carrier per second
const SCOUT_DEP = 0.5;    // forager deposit per organism per second
const TRAIL_MAX = 1;
const DRAW_MIN = 0.05;    // skip near-empty tiles when painting

const STORAGE_CAP = 220;  // foodStore mapped to a full granary
const COURIER_CAP = 90;
const LARVA_DUR = 1.6;    // seconds nest→hive
const GENE_DUR = 1.9;     // seconds field→nest
const EMIT_CAP = 5;       // couriers emitted per colony per frame

// --- Persistent supply ROUTES (long-memory traffic) -------------------------
// The pheromone `food` field above spikes fast and fades in seconds — it shows
// where carriers ARE right now. A supply ROUTE is the opposite: it is built from
// SUSTAINED traffic over tens of seconds, so a corridor used over and over
// hardens into a persistent highway while a one-off detour never qualifies. The
// route field integrates the food field with a slow leak; tiers/width/brightness
// all read off its normalized value.
const ROUTE_TAU = 32;     // route persistence (s) — slow, so corridors accrete
const ROUTE_GAIN = 0.45;  // how fast laden traffic feeds the route accumulator
const ROUTE_MAX = 1;      // route value is clamped to [0,1]
const TIER_SECONDARY = 0.10; // ≥ this normalized value ⇒ Secondary route
const TIER_PRIMARY = 0.32;   // ≥ this ⇒ Primary route
const TIER_MAJOR = 0.66;     // ≥ this ⇒ Major Supply Route
const THRU_TAU = 4;       // throughput EMA time-constant (s)

// Bottlenecks: a heavy-traffic tile that is also a CONSTRICTION (few passable
// neighbours), so supply is forced through a narrow gap. Scanned on a cadence,
// not every frame, into a fixed-size strongest-first list.
const BOTTLENECK_CAP = 12;        // max flagged bottlenecks tracked at once
const BOTTLENECK_SCAN = 0.5;      // seconds between bottleneck scans
const BOTTLENECK_OPEN_MAX = 4;    // ≤ this many of 8 neighbours passable ⇒ constriction

// Per-tier draw style: [r,g,b], footprint scale (×cell), base alpha. Cool→gold
// ramp with rising size so the supply hierarchy reads at a glance.
const TIER_STYLE: ReadonlyArray<{ r: number; g: number; b: number; size: number; alpha: number }> = [
  { r: 0, g: 0, b: 0, size: 0, alpha: 0 },             // 0 = none (unused)
  { r: 96, g: 165, b: 214, size: 0.55, alpha: 0.30 },  // 1 = Secondary — faint cool
  { r: 132, g: 214, b: 178, size: 0.95, alpha: 0.45 }, // 2 = Primary — brighter teal-green
  { r: 255, g: 196, b: 86, size: 1.5, alpha: 0.62 },   // 3 = Major — wide gold glow
];

/** RGB swatch colours (CSS) per tier, exported so the HUD legend matches the map. */
export const ROUTE_TIER_COLORS: readonly string[] = [
  'transparent',
  'rgb(96,165,214)',
  'rgb(132,214,178)',
  'rgb(255,196,86)',
];

/**
 * Classify a normalized route value (0..1) into a supply tier.
 * 0 = none, 1 = Secondary, 2 = Primary, 3 = Major Supply Route. Pure + exported
 * so the HUD and tests share the exact thresholds the canvas paints with.
 */
export function routeTier(v: number): 0 | 1 | 2 | 3 {
  if (v >= TIER_MAJOR) return 3;
  if (v >= TIER_PRIMARY) return 2;
  if (v >= TIER_SECONDARY) return 1;
  return 0;
}

/**
 * Route-efficiency from a route field: the share of total route "mass" carried on
 * the strong (Major+Primary) tiles vs. all route mass, as 0..100. High ⇒ traffic
 * concentrated on a few highways (efficient supply); low ⇒ diffuse wandering.
 * Pure + exported for unit testing; divide-by-zero guarded (no mass ⇒ 0).
 */
export function routeEfficiencyOf(route: Float32Array): number {
  let strong = 0;
  let total = 0;
  for (let i = 0; i < route.length; i++) {
    const v = route[i] as number;
    if (v < TIER_SECONDARY) continue; // ignore noise floor
    total += v;
    if (v >= TIER_PRIMARY) strong += v;
  }
  if (total <= 0) return 0;
  return Math.round((strong / total) * 100);
}

interface Courier {
  sx: number; sy: number; tx: number; ty: number;
  t: number; dur: number; kind: 0 | 1; cid: number; // kind 0 = larva, 1 = gene
}

/** A flagged supply bottleneck: a heavy-traffic constriction tile. */
interface Bottleneck { x: number; y: number; strength: number; }

export class LogisticsView {
  readonly w: number;
  readonly h: number;
  private readonly food: Float32Array;
  private readonly scout: Float32Array;
  private readonly route: Float32Array;   // long-memory traffic accumulator (ALL colonies)
  private readonly prevPop: Int32Array;
  private readonly prevGenes: Int32Array;
  private readonly couriers: Courier[] = [];
  private seeded = false;

  // PLAYER-ONLY supply view. The shared `food`/`route` fields above aggregate
  // every colony's traffic and still drive the canvas trail/route DRAW (the whole
  // map's logistics is worth seeing). But the HUD METRICS — efficiency, tier
  // counts, throughput, bottlenecks — are about the PLAYER's own supply lines, so
  // they read these parallel fields fed ONLY by PLAYER_COLONY carriers. Same
  // sizing + decay as the shared fields, so a player-only network still develops
  // and decays identically; reused buffers ⇒ no per-frame allocation.
  private readonly pfood: Float32Array;   // player laden-trail (drives proute)
  private readonly proute: Float32Array;  // player long-memory route accumulator

  // Throughput: an EMA of laden deposits/sec (the rate workers are hauling).
  private laden = 0;            // laden carriers (all colonies) counted this frame
  private thru = 0;            // smoothed laden-deposits/sec (all colonies)
  private pthru = 0;           // smoothed laden-deposits/sec (player only)

  // Bottlenecks: a fixed, reused list (no per-frame allocation) refreshed on a
  // cadence; `bnCount` is how many of the slots are live this scan.
  private readonly bottlenecks: Bottleneck[];
  private bnCount = 0;
  private bnClock = 0;         // seconds since the last bottleneck scan
  private pulse = 0;           // time accumulator driving the warning-ring pulse

  constructor(w: number, h: number, maxColonies: number) {
    this.w = w;
    this.h = h;
    this.food = new Float32Array(w * h);
    this.scout = new Float32Array(w * h);
    this.route = new Float32Array(w * h);
    this.pfood = new Float32Array(w * h);
    this.proute = new Float32Array(w * h);
    this.prevPop = new Int32Array(maxColonies);
    this.prevGenes = new Int32Array(maxColonies);
    this.bottlenecks = new Array(BOTTLENECK_CAP);
    for (let i = 0; i < BOTTLENECK_CAP; i++) this.bottlenecks[i] = { x: 0, y: 0, strength: 0 };
  }

  /** Advance trails + couriers from the live sim. `dt` is seconds since last frame. */
  update(session: GameSession, dt: number): void {
    if (dt <= 0) return;
    const world = session.world;
    const ents = world.entities;
    const w = this.w;

    // 1) Decay all fields (frame-rate independent exponential fade). The player
    //    laden-trail decays on the same FOOD_TAU as the shared one.
    const fdec = Math.exp(-dt / FOOD_TAU);
    const sdec = Math.exp(-dt / SCOUT_TAU);
    const food = this.food;
    const scout = this.scout;
    const pfood = this.pfood;
    for (let i = 0; i < food.length; i++) {
      food[i] = (food[i] as number) * fdec;
      scout[i] = (scout[i] as number) * sdec;
      pfood[i] = (pfood[i] as number) * fdec;
    }

    // 2) Deposit: laden carriers lay the food trail, everyone else the forage
    //    trail. Player-colony laden carriers ALSO lay the player-only trail, so
    //    the HUD metrics see just the player's own supply traffic.
    const fdep = FOOD_DEP * dt;
    const sdep = SCOUT_DEP * dt;
    const active = ents.activeView();
    const n = ents.aliveCount;
    let laden = 0;
    let pladen = 0;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      if ((ents.kind[id] as number) !== KIND.Organism) continue;
      const idx = (ents.posY[id] as number) * w + (ents.posX[id] as number);
      if ((ents.cargo[id] as number) > 0) {
        food[idx] = Math.min(TRAIL_MAX, (food[idx] as number) + fdep);
        laden++;
        if ((ents.colony[id] as number) === PLAYER_COLONY) {
          pfood[idx] = Math.min(TRAIL_MAX, (pfood[idx] as number) + fdep);
          pladen++;
        }
      } else {
        scout[idx] = Math.min(TRAIL_MAX, (scout[idx] as number) + sdep);
      }
    }
    this.laden = laden;

    // 2b) Integrate the persistent ROUTE fields from the (just-updated) food
    //     fields: route ← route·decay + food·gain (clamped). Slow decay
    //     (ROUTE_TAU) so a corridor used frequently accretes into a highway and an
    //     abandoned one fades only gradually. Frame-rate independent via
    //     exp(-dt/τ). The shared `route` underlies the canvas draw; `proute` (fed
    //     from the player trail only) underlies the HUD metrics + bottleneck scan.
    const rdec = Math.exp(-dt / ROUTE_TAU);
    const rgain = ROUTE_GAIN * dt;
    const route = this.route;
    const proute = this.proute;
    for (let i = 0; i < route.length; i++) {
      const v = (route[i] as number) * rdec + (food[i] as number) * rgain;
      route[i] = v > ROUTE_MAX ? ROUTE_MAX : v;
      const pv = (proute[i] as number) * rdec + (pfood[i] as number) * rgain;
      proute[i] = pv > ROUTE_MAX ? ROUTE_MAX : pv;
    }

    // 2c) Throughput EMA: smooth laden-deposits/sec so the HUD reads a stable
    //     rate. Track both the all-colony rate and the player-only rate.
    const a = 1 - Math.exp(-dt / THRU_TAU);
    this.thru += (laden - this.thru) * a;
    this.pthru += (pladen - this.pthru) * a;

    // 3) Couriers from REAL per-colony deltas: population up ⇒ larva moved, gene
    //    count up ⇒ loot ferried home. Seed silently on the first frame.
    const colonies = world.colonies;
    for (let cid = 0; cid < colonies.count; cid++) {
      const pop = colonies.populationAt(cid);
      const gn = world.genes.countOf(cid);
      if (this.seeded) {
        const dp = pop - (this.prevPop[cid] as number);
        if (dp > 0) this.emitLarva(world, cid, Math.min(dp, EMIT_CAP));
        const dg = gn - (this.prevGenes[cid] as number);
        if (dg > 0) this.emitGene(world, cid, Math.min(dg, EMIT_CAP));
      }
      this.prevPop[cid] = pop;
      this.prevGenes[cid] = gn;
    }
    this.seeded = true;

    // 4) Advance couriers; retire on arrival.
    const cs = this.couriers;
    for (let i = cs.length - 1; i >= 0; i--) {
      const c = cs[i] as Courier;
      c.t += dt / c.dur;
      if (c.t >= 1) cs.splice(i, 1);
    }

    // 5) Bottleneck scan (throttled) + warning-ring pulse clock.
    this.pulse += dt;
    this.bnClock += dt;
    if (this.bnClock >= BOTTLENECK_SCAN) {
      this.bnClock = 0;
      this.scanBottlenecks(world.grid);
    }
  }

  /**
   * Refresh the bottleneck list: scan tiles whose PLAYER route value is ≥ Primary
   * and which are CONSTRICTIONS (≤ BOTTLENECK_OPEN_MAX of 8 neighbours passable),
   * so the player's supply is squeezed through a narrow gap. Keeps the strongest
   * BOTTLENECK_CAP in the preallocated list (insertion sort into the bounded array
   * — no temp allocation, cheap since the list is tiny). Throttled by the caller.
   */
  private scanBottlenecks(grid: GameSession['world']['grid']): void {
    const route = this.proute;
    const w = this.w;
    const h = this.h;
    const list = this.bottlenecks;
    let count = 0;
    let minStrength = Infinity; // smallest strength currently in the list
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        const v = route[row + x] as number;
        if (v < TIER_PRIMARY) continue;
        if (!grid.isPassable(x, y)) continue;
        // Count passable neighbours (8-connected, bounds as walls).
        let open = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < w && ny >= 0 && ny < h && grid.isPassable(nx, ny)) open++;
          }
        }
        if (open > BOTTLENECK_OPEN_MAX) continue; // not a constriction
        // Strength weights traffic by tightness: tighter gaps rank higher.
        const strength = v * (1 + (BOTTLENECK_OPEN_MAX - open) * 0.25);
        if (count < BOTTLENECK_CAP) {
          const slot = list[count] as Bottleneck;
          slot.x = x; slot.y = y; slot.strength = strength;
          count++;
          if (count === BOTTLENECK_CAP) minStrength = this.weakestStrength(count);
        } else if (strength > minStrength) {
          // Replace the current weakest, then recompute the floor.
          this.replaceWeakest(count, x, y, strength);
          minStrength = this.weakestStrength(count);
        }
      }
    }
    this.bnCount = count;
  }

  private weakestStrength(count: number): number {
    const list = this.bottlenecks;
    let m = Infinity;
    for (let i = 0; i < count; i++) {
      const s = (list[i] as Bottleneck).strength;
      if (s < m) m = s;
    }
    return m;
  }

  private replaceWeakest(count: number, x: number, y: number, strength: number): void {
    const list = this.bottlenecks;
    let wi = 0;
    let m = Infinity;
    for (let i = 0; i < count; i++) {
      const s = (list[i] as Bottleneck).strength;
      if (s < m) { m = s; wi = i; }
    }
    const slot = list[wi] as Bottleneck;
    slot.x = x; slot.y = y; slot.strength = strength;
  }

  // --- metrics (pure reads of the PLAYER route field, for the HUD) ------------
  // These describe the PLAYER colony's own logistics (not the whole map), so they
  // read the player-only `proute`/`pthru` fields. The canvas DRAW below still uses
  // the shared all-colony fields.

  /** 0..100: share of player route mass on Major+Primary tiles (supply concentration). */
  routeEfficiency(): number {
    return routeEfficiencyOf(this.proute);
  }

  /** Player tile counts per tier (each tile is counted in exactly one tier). */
  routeCounts(): { secondary: number; primary: number; major: number } {
    const route = this.proute;
    let secondary = 0;
    let primary = 0;
    let major = 0;
    for (let i = 0; i < route.length; i++) {
      const t = routeTier(route[i] as number);
      if (t === 3) major++;
      else if (t === 2) primary++;
      else if (t === 1) secondary++;
    }
    return { secondary, primary, major };
  }

  /** Smoothed player laden-carrier throughput (deposits/sec), rounded for display. */
  throughput(): number {
    return Math.round(this.pthru);
  }

  /** Count of currently-flagged supply bottlenecks. */
  bottleneckCount(): number {
    return this.bnCount;
  }

  private push(c: Courier): void {
    if (this.couriers.length >= COURIER_CAP) this.couriers.shift();
    this.couriers.push(c);
  }

  private emitLarva(world: GameSession['world'], cid: number, count: number): void {
    const nx = world.colonies.homeXAt(cid);
    const ny = world.colonies.homeYAt(cid);
    const hives = world.ai.hiveCountAt(cid);
    for (let k = 0; k < count; k++) {
      let tx = nx + (Math.random() * 4 - 2);
      let ty = ny + (Math.random() * 4 - 2);
      if (hives > 0) {
        const h = Math.floor(Math.random() * hives);
        tx = world.ai.hiveXAt(cid, h);
        ty = world.ai.hiveYAt(cid, h);
      }
      this.push({ sx: nx, sy: ny, tx, ty, t: Math.random() * 0.15, dur: LARVA_DUR, kind: 0, cid });
    }
  }

  private emitGene(world: GameSession['world'], cid: number, count: number): void {
    const nx = world.colonies.homeXAt(cid);
    const ny = world.colonies.homeYAt(cid);
    const hives = world.ai.hiveCountAt(cid);
    for (let k = 0; k < count; k++) {
      let sx: number;
      let sy: number;
      if (hives > 0 && Math.random() < 0.6) {
        const h = Math.floor(Math.random() * hives);
        sx = world.ai.hiveXAt(cid, h);
        sy = world.ai.hiveYAt(cid, h);
      } else {
        const ang = Math.random() * Math.PI * 2;
        const dist = 6 + Math.random() * 6;
        sx = Math.max(0, Math.min(this.w - 1, nx + Math.cos(ang) * dist));
        sy = Math.max(0, Math.min(this.h - 1, ny + Math.sin(ang) * dist));
      }
      this.push({ sx, sy, tx: nx, ty: ny, t: Math.random() * 0.1, dur: GENE_DUR, kind: 1, cid });
    }
  }

  // --- drawing ---------------------------------------------------------------

  /** Pheromone heatmap: forager trail (cool) under the laden food highway (warm). */
  drawTrails(ctx: CanvasRenderingContext2D, view: ViewBox): void {
    const { cell, offX, offY } = view;
    const w = this.w;
    const h = this.h;
    const food = this.food;
    const scout = this.scout;
    const sz = cell + 1;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let y = 0; y < h; y++) {
      const row = y * w;
      const py = offY + y * cell - 0.5;
      for (let x = 0; x < w; x++) {
        const s = scout[row + x] as number;
        if (s > DRAW_MIN) {
          ctx.fillStyle = `rgba(78,150,214,${(s * 0.4).toFixed(3)})`;
          ctx.fillRect(offX + x * cell - 0.5, py, sz, sz);
        }
        const f = food[row + x] as number;
        if (f > DRAW_MIN) {
          ctx.fillStyle = `rgba(255,196,86,${(f * 0.6).toFixed(3)})`;
          ctx.fillRect(offX + x * cell - 0.5, py, sz, sz);
        }
      }
    }
    ctx.restore();
  }

  /**
   * Paint the PERSISTENT supply-route network by tier. Unlike the live pheromone
   * glow (`drawTrails`), this reads the long-memory route field, so only corridors
   * with SUSTAINED traffic show — and they show as a hierarchy: Secondary is a
   * small faint cool mark, Primary a medium brighter one, Major a WIDE bright gold
   * glow (full cell + bloom). Both width AND brightness rise with tier, so heavier
   * routes are unmistakably stronger. Drawn under the live trail layer.
   */
  drawRoutes(ctx: CanvasRenderingContext2D, view: ViewBox): void {
    const { cell, offX, offY } = view;
    const w = this.w;
    const h = this.h;
    const route = this.route;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let y = 0; y < h; y++) {
      const row = y * w;
      const cy = offY + y * cell + cell / 2;
      for (let x = 0; x < w; x++) {
        const v = route[row + x] as number;
        const tier = routeTier(v);
        if (tier === 0) continue;
        const st = TIER_STYLE[tier] as { r: number; g: number; b: number; size: number; alpha: number };
        const cx = offX + x * cell + cell / 2;
        // Footprint + alpha both scale with tier AND with how far the tile is into
        // its band, so a near-Major Primary reads slightly stronger than a faint one.
        const sz = cell * st.size;
        const a = (st.alpha * Math.min(1, 0.55 + v * 0.6)).toFixed(3);
        ctx.fillStyle = `rgba(${st.r},${st.g},${st.b},${a})`;
        ctx.fillRect(cx - sz / 2, cy - sz / 2, sz, sz);
        // Major routes get an extra soft bloom so highways glow.
        if (tier === 3) {
          const br = sz * 1.55;
          ctx.fillStyle = `rgba(${st.r},${st.g},${st.b},${(st.alpha * 0.35).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(cx, cy, br / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }

  /**
   * Draw a pulsing warning ring (+ ⚠) at each flagged supply bottleneck — a
   * heavy-traffic tile squeezed through a narrow gap. Pulse driven by the time
   * accumulator so it breathes. Drawn ABOVE organisms so the alert is never lost.
   */
  drawBottlenecks(ctx: CanvasRenderingContext2D, view: ViewBox): void {
    const { cell, offX, offY } = view;
    const n = this.bnCount;
    if (n === 0) return;
    const list = this.bottlenecks;
    const pulse = 0.5 + 0.5 * Math.sin(this.pulse * 4.2); // 0..1 breathing
    ctx.save();
    for (let i = 0; i < n; i++) {
      const b = list[i] as Bottleneck;
      const cx = offX + b.x * cell + cell / 2;
      const cy = offY + b.y * cell + cell / 2;
      const baseR = Math.max(4, cell * 0.75);
      const r = baseR * (0.85 + pulse * 0.35);
      // Warning ring.
      ctx.strokeStyle = `rgba(255,90,90,${(0.45 + pulse * 0.45).toFixed(3)})`;
      ctx.lineWidth = Math.max(1.4, cell * 0.16);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      // Soft inner halo.
      ctx.fillStyle = `rgba(255,90,90,${(0.10 + pulse * 0.10).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
      ctx.fill();
      // ⚠ glyph when cells are large enough to read it.
      if (cell >= 7) {
        ctx.fillStyle = `rgba(255,225,120,${(0.7 + pulse * 0.3).toFixed(3)})`;
        ctx.font = `${Math.max(8, Math.floor(cell * 0.9))}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⚠', cx, cy + cell * 0.04);
      }
    }
    ctx.restore();
  }

  /** A granary at each nest that fills with the colony's food stockpile. */
  drawStorage(ctx: CanvasRenderingContext2D, view: ViewBox, session: GameSession): void {
    const { cell, offX, offY } = view;
    const colonies = session.world.colonies;
    const bw = Math.max(5, cell * 0.95);
    const bh = Math.max(7, cell * 1.4);
    for (let cid = 0; cid < colonies.count; cid++) {
      const frac = Math.max(0, Math.min(1, colonies.foodAt(cid) / STORAGE_CAP));
      const hx = colonies.homeXAt(cid);
      const hy = colonies.homeYAt(cid);
      const bx = offX + hx * cell + cell / 2 - bw / 2;
      let by = offY + hy * cell - bh - 2;
      if (by < offY) by = offY + hy * cell + cell + 2; // flip below if near top edge
      // body
      ctx.fillStyle = 'rgba(8,11,16,0.78)';
      ctx.fillRect(bx, by, bw, bh);
      // fill from the bottom
      const fillH = bh * frac;
      ctx.fillStyle = colonyColor(cid);
      ctx.globalAlpha = cid === 0 ? 0.95 : 0.7;
      ctx.fillRect(bx, by + bh - fillH, bw, fillH);
      ctx.globalAlpha = 1;
      // outline
      ctx.strokeStyle = colonyColor(cid);
      ctx.lineWidth = 1;
      ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
    }
  }

  /** Larva + gene courier motes travelling between nest and hive nodes. */
  drawCouriers(ctx: CanvasRenderingContext2D, view: ViewBox): void {
    const { cell, offX, offY } = view;
    const r = Math.max(1.6, cell * 0.24);
    for (const c of this.couriers) {
      const e = c.t * c.t * (3 - 2 * c.t); // smoothstep ease
      const x = c.sx + (c.tx - c.sx) * e;
      const y = c.sy + (c.ty - c.sy) * e;
      const px = offX + x * cell + cell / 2;
      const py = offY + y * cell + cell / 2;
      const a = c.t > 0.85 ? Math.max(0, 1 - (c.t - 0.85) / 0.15) : 1;
      if (c.kind === 0) {
        // larva: pale capsule with a soft halo
        ctx.fillStyle = `rgba(232,236,255,${(a * 0.25).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(px, py, r * 1.9, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(245,247,255,${a.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
      } else {
        // gene: warm diamond
        ctx.fillStyle = `rgba(255,210,90,${(a * 0.3).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(px, py, r * 1.9, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(255,214,102,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(px, py - r); ctx.lineTo(px + r, py);
        ctx.lineTo(px, py + r); ctx.lineTo(px - r, py);
        ctx.closePath(); ctx.fill();
      }
    }
  }
}
